'use client'

import {
    PayPalButtons,
    PayPalCardFieldsProvider,
    PayPalCVVField,
    PayPalExpiryField,
    PayPalNameField,
    PayPalNumberField,
    PayPalScriptProvider,
    usePayPalCardFields,
    usePayPalScriptReducer
} from '@paypal/react-paypal-js'
import { Button } from '@tebuto/buttons/Button'
import { ComponentSpinner } from '@tebuto/layout/Spinner'
import { cn } from '@tebuto/lib/utils'
import type { WalletCheckoutPricing } from '@tebuto/payment/paypal/wallet-checkout-pricing'
import { useRuntimeEnv } from '@tebuto/providers/RuntimeEnvProvider'
import type { OnlinePaymentMethods } from '@tebuto/widget/booking/types'
import { WidgetButton } from '@tebuto/widget/WidgetButton'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import ApplePayButton from './Buttons/ApplePay'
import GooglePayButton from './Buttons/GooglePay'
import { getPayPalScriptId, registerPayPalSdkMount, scheduleTeardownPayPalSdk } from './paypal-script-lifecycle'

// Brand button height matching PayPal's native button
const BUTTON_HEIGHT = 45
const WIDGET_PAYPAL_CARD_FIELD_CLASS = 'widget-paypal-card-field'

const payPalCheckoutMountGenerationRef = { current: 0 }

function nextPayPalCheckoutMountKey(): number {
    payPalCheckoutMountGenerationRef.current += 1
    return payPalCheckoutMountGenerationRef.current
}

function resolvePayPalSdkNamespace(dataNamespace?: string): string {
    return typeof dataNamespace === 'string' && dataNamespace.length > 0 ? dataNamespace : 'paypal'
}

function isPayPalSdkComponentAvailable(dataNamespace: string | undefined, component: string): boolean {
    if (typeof window === 'undefined') {
        return false
    }

    const namespace = resolvePayPalSdkNamespace(dataNamespace)
    const sdk = (window as unknown as Record<string, Record<string, unknown> | undefined>)[namespace]
    return sdk?.[component] !== undefined
}

function buildPayPalCardFieldsStyle(useWidgetButton: boolean) {
    const sharedInputStyle = {
        border: useWidgetButton ? '1px solid var(--widget-border, #e5e7eb)' : '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        outline: 'none',
        boxShadow: 'none'
    }

    return {
        input: {
            ...sharedInputStyle,
            'font-size': '14px',
            'font-family': useWidgetButton
                ? 'inherit'
                : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            color: useWidgetButton ? 'var(--widget-text-primary, #374151)' : '#374151',
            padding: useWidgetButton ? '8px 12px' : '10px 12px'
        },
        '.invalid': {
            color: useWidgetButton ? 'var(--widget-text-primary, #374151)' : '#374151'
        }
    }
}

function PayPalCardFieldLabel({ children, useWidgetButton }: { children: string; useWidgetButton: boolean }) {
    return (
        <span
            className={cn(
                'mb-1 block text-sm',
                useWidgetButton ? 'widget-text-secondary' : 'text-xs font-medium text-gray-600'
            )}
        >
            {children}
        </span>
    )
}

function PayPalCardFieldsSection({ useWidgetButton }: { useWidgetButton: boolean }) {
    const fieldClassName = useWidgetButton ? WIDGET_PAYPAL_CARD_FIELD_CLASS : undefined

    return (
        <div className="flex flex-col gap-3">
            <div>
                <PayPalCardFieldLabel useWidgetButton={useWidgetButton}>Karteninhaber</PayPalCardFieldLabel>
                <PayPalNameField placeholder="Name auf der Karte" className={fieldClassName} />
            </div>
            <div>
                <PayPalCardFieldLabel useWidgetButton={useWidgetButton}>Kartennummer</PayPalCardFieldLabel>
                <PayPalNumberField placeholder="1234 5678 9012 3456" className={fieldClassName} />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <PayPalCardFieldLabel useWidgetButton={useWidgetButton}>Gültig bis</PayPalCardFieldLabel>
                    <PayPalExpiryField placeholder="MM/JJ" className={fieldClassName} />
                </div>
                <div>
                    <PayPalCardFieldLabel useWidgetButton={useWidgetButton}>CVV</PayPalCardFieldLabel>
                    <PayPalCVVField placeholder="123" className={fieldClassName} />
                </div>
            </div>
            <PayPalCardSubmitButton useWidgetButton={useWidgetButton} />
        </div>
    )
}

interface PayPalCheckoutProps {
    payPalClientToken: string
    onlinePaymentMethods: OnlinePaymentMethods
    selectedMethod?: OnlinePaymentMethods[number] // If provided, only render this method's UI
    pricing: WalletCheckoutPricing
    handleCreateOrder: () => Promise<string>
    handleApprove: (data: { orderID: string }) => Promise<void>
    handleError?: () => void
    isPayPalLoading: boolean
    cardFormOnly?: boolean
    useWidgetButton?: boolean
}

export default function PayPalCheckout(props: PayPalCheckoutProps) {
    const { payPalClientToken, onlinePaymentMethods } = props
    const runtimeEnv = useRuntimeEnv()
    const mountKeyRef = useRef<number | null>(null)
    if (mountKeyRef.current === null) {
        mountKeyRef.current = nextPayPalCheckoutMountKey()
    }

    // Filter out SEPA from online payment methods
    const filteredMethods = useMemo(
        () => onlinePaymentMethods.filter((m) => m !== 'sepa') as OnlinePaymentMethods,
        [onlinePaymentMethods]
    )

    const components = useMemo(() => {
        const components = []

        // Only include paypal buttons (not sepa)
        if (filteredMethods.includes('paypal')) {
            components.push('buttons')
        }

        if (filteredMethods.includes('card')) {
            components.push('card-fields')
        }

        if (filteredMethods.includes('google_pay')) {
            components.push('googlepay')
        }

        if (filteredMethods.includes('apple_pay')) {
            components.push('applepay')
        }

        return components
    }, [filteredMethods])

    const scriptOptions = useMemo(
        () => ({
            clientId: runtimeEnv.payPal.clientId,
            dataClientToken: payPalClientToken,
            currency: 'EUR',
            locale: 'de_DE',
            intent: 'capture',
            components: components.join(',')
        }),
        [runtimeEnv.payPal.clientId, payPalClientToken, components]
    )

    const scriptOptionsRef = useRef(scriptOptions)
    scriptOptionsRef.current = scriptOptions

    const providerKey = useMemo(() => `${getPayPalScriptId(scriptOptions)}-${mountKeyRef.current}`, [scriptOptions])

    // Deferred mount gate: give a just-unmounted sibling's scheduled teardown a frame to settle
    // before bootstrapping the script provider, which protects against React Strict Mode's
    // mount/unmount/mount double-invoke racing the SDK's own global (zoid) bootstrap.
    const [sdkMountAllowed, setSdkMountAllowed] = useState(false)

    useEffect(() => {
        registerPayPalSdkMount(scriptOptionsRef.current)
        const frameId = requestAnimationFrame(() => setSdkMountAllowed(true))

        return () => {
            cancelAnimationFrame(frameId)
            scheduleTeardownPayPalSdk(scriptOptionsRef.current)
        }
    }, [])

    if (!sdkMountAllowed) {
        return <ComponentSpinner />
    }

    return (
        <PayPalScriptProvider key={providerKey} options={scriptOptions}>
            <PaymentButtons {...props} onlinePaymentMethods={filteredMethods} />
        </PayPalScriptProvider>
    )
}

function PaymentButtons({
    onlinePaymentMethods,
    selectedMethod,
    pricing,
    handleCreateOrder,
    handleApprove,
    handleError: onError,
    isPayPalLoading,
    cardFormOnly,
    useWidgetButton = false
}: PayPalCheckoutProps) {
    // If selectedMethod is provided, only render that method; otherwise render all available methods
    const methodsToRender = useMemo(
        () =>
            (selectedMethod
                ? [selectedMethod].filter((m) => m !== 'sepa')
                : onlinePaymentMethods.filter((m) => m !== 'sepa')) as OnlinePaymentMethods,
        [selectedMethod, onlinePaymentMethods]
    )
    const [{ isResolved, isRejected, isPending, options: scriptOptions }] = usePayPalScriptReducer()

    // Track which buttons are configured and ready
    const [buttonReadyStates, setButtonReadyStates] = useState<Record<string, boolean>>({})

    useEffect(() => {
        if (!isResolved) {
            setButtonReadyStates({})
        }
    }, [isResolved, getPayPalScriptId(scriptOptions)])

    // Determine which buttons should be loaded - only those that will actually be rendered
    const expectedButtons = useMemo(() => {
        const buttons: string[] = []
        if (methodsToRender.includes('paypal')) {
            buttons.push('paypal-buttons')
        }
        if (methodsToRender.includes('card')) {
            buttons.push('card-fields')
        }
        if (methodsToRender.includes('google_pay')) {
            buttons.push('google_pay')
        }
        if (methodsToRender.includes('apple_pay')) {
            buttons.push('apple_pay')
        }
        return buttons
    }, [methodsToRender])

    // Check if all expected buttons are ready
    const allButtonsReady = useMemo(() => {
        if (expectedButtons.length === 0) return true
        return expectedButtons.every((button) => buttonReadyStates[button] === true)
    }, [expectedButtons, buttonReadyStates])

    const handlePayPalButtonsReady = useCallback(() => {
        setButtonReadyStates((prev) => ({ ...prev, 'paypal-buttons': true }))
    }, [])

    const [paypalWalletButtonsReady, setPaypalWalletButtonsReady] = useState(false)

    useEffect(() => {
        if (!isResolved || !methodsToRender.includes('paypal')) {
            setPaypalWalletButtonsReady(false)
            return
        }

        if (isPayPalSdkComponentAvailable(scriptOptions.dataNamespace, 'Buttons')) {
            setPaypalWalletButtonsReady(true)
            return
        }

        setPaypalWalletButtonsReady(false)
        const intervalId = window.setInterval(() => {
            if (isPayPalSdkComponentAvailable(scriptOptions.dataNamespace, 'Buttons')) {
                setPaypalWalletButtonsReady(true)
                window.clearInterval(intervalId)
            }
        }, 50)

        return () => {
            window.clearInterval(intervalId)
        }
    }, [isResolved, methodsToRender, scriptOptions.dataNamespace, getPayPalScriptId(scriptOptions)])

    useEffect(() => {
        if (!isResolved || !methodsToRender.includes('card')) {
            return
        }

        if (isPayPalSdkComponentAvailable(scriptOptions.dataNamespace, 'CardFields')) {
            setButtonReadyStates((prev) => ({ ...prev, 'card-fields': true }))
            return
        }

        const intervalId = window.setInterval(() => {
            if (isPayPalSdkComponentAvailable(scriptOptions.dataNamespace, 'CardFields')) {
                setButtonReadyStates((prev) => ({ ...prev, 'card-fields': true }))
                window.clearInterval(intervalId)
            }
        }, 50)

        return () => {
            window.clearInterval(intervalId)
        }
    }, [isResolved, methodsToRender, scriptOptions.dataNamespace, getPayPalScriptId(scriptOptions)])

    const handleGooglePayReady = useCallback(() => {
        setButtonReadyStates((prev) => ({ ...prev, google_pay: true }))
    }, [])

    const handleApplePayReady = useCallback(() => {
        setButtonReadyStates((prev) => ({ ...prev, apple_pay: true }))
    }, [])

    const isLoading = useMemo(
        () => isPayPalLoading || isPending || !allButtonsReady,
        [isPayPalLoading, isPending, allButtonsReady]
    )

    const handleError = useCallback(
        (error: unknown): void => {
            console.error('PayPal error (raw):', error)
            console.error('PayPal error (stringified):', JSON.stringify(error, null, 2))

            // Helper to extract details from various error structures
            const extractDetails = (
                obj: unknown
            ): Array<{ field?: string; issue?: string; description?: string }> | undefined => {
                if (!obj || typeof obj !== 'object') return undefined
                const o = obj as Record<string, unknown>

                // Direct details array
                if (Array.isArray(o.details)) return o.details

                // Nested in body
                if (o.body && typeof o.body === 'object') {
                    const body = o.body as Record<string, unknown>
                    if (Array.isArray(body.details)) return body.details
                }

                // Nested in data.body (PayPal SDK error structure)
                if (o.data && typeof o.data === 'object') {
                    const data = o.data as Record<string, unknown>
                    if (Array.isArray(data.details)) return data.details
                    // Check data.body.details
                    if (data.body && typeof data.body === 'object') {
                        const dataBody = data.body as Record<string, unknown>
                        if (Array.isArray(dataBody.details)) return dataBody.details
                    }
                }

                // Nested in response
                if (o.response && typeof o.response === 'object') {
                    const response = o.response as Record<string, unknown>
                    if (Array.isArray(response.details)) return response.details
                    if (response.data && typeof response.data === 'object') {
                        const respData = response.data as Record<string, unknown>
                        if (Array.isArray(respData.details)) return respData.details
                    }
                }

                return undefined
            }

            // Try to parse if string
            let errorObj: unknown = error
            if (typeof error === 'string') {
                try {
                    errorObj = JSON.parse(error)
                } catch {
                    // Not JSON
                }
            }

            // Check for details array in various structures
            const details = extractDetails(errorObj)
            console.log('Extracted details:', details)

            if (details && Array.isArray(details)) {
                const validationErrors = details.filter((d) => d.issue === 'VALIDATION_ERROR')
                if (validationErrors.length > 0) {
                    const messages = validationErrors.map((d) => {
                        const field = d.field?.toLowerCase() || ''
                        if (field.includes('number')) return 'Ungültige Kartennummer'
                        if (field.includes('expiry') || field.includes('expiration')) return 'Ungültiges Ablaufdatum'
                        if (field.includes('security_code') || field.includes('cvv'))
                            return 'Ungültiger Sicherheitscode (CVV)'
                        if (field.includes('name')) return 'Ungültiger Karteninhaber'
                        return d.description || 'Validierungsfehler'
                    })
                    toast.error(messages.join('. '))
                    onError?.()
                    return
                }
            }

            toast.error('Die Zahlung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.')
            onError?.()
        },
        [onError]
    )

    const handleCancel = useCallback((): void => {
        // No action needed on cancel, just let the user try again
        onError?.()
    }, [onError])

    useEffect(() => {
        if (isRejected) {
            handleError(new Error('PayPal SDK konnte nicht geladen werden'))
        }
    }, [isRejected, handleError])

    // Card form only mode - just render the card fields
    if (cardFormOnly) {
        if (!isResolved) {
            return <ComponentSpinner />
        }

        if (isRejected) {
            return <div className="text-sm text-red-600 p-2">Kreditkartenformular konnte nicht geladen werden.</div>
        }

        if (!buttonReadyStates['card-fields']) {
            return <ComponentSpinner />
        }

        return (
            <PayPalCardFieldsProvider
                createOrder={handleCreateOrder}
                onApprove={handleApprove}
                onError={handleError}
                onCancel={handleCancel}
                style={buildPayPalCardFieldsStyle(useWidgetButton)}
            >
                <PayPalCardFieldsSection useWidgetButton={useWidgetButton} />
            </PayPalCardFieldsProvider>
        )
    }

    // Single method button rendering for the new Payment UI
    if (selectedMethod) {
        if (!isResolved) {
            return <BrandButtonLoading method={selectedMethod} />
        }

        if (isRejected) {
            return <BrandButtonError method={selectedMethod} />
        }

        // PayPal - render native PayPal button
        if (selectedMethod === 'paypal') {
            if (!paypalWalletButtonsReady) {
                return <BrandButtonLoading method={selectedMethod} />
            }

            return (
                <div className="flex flex-col gap-2" data-testid="booking-payment-paypal-action">
                    <PayPalButtons
                        createOrder={handleCreateOrder}
                        onApprove={handleApprove}
                        onError={handleError}
                        onCancel={handleCancel}
                        onInit={handlePayPalButtonsReady}
                        disabled={isLoading}
                        style={{
                            disableMaxWidth: true,
                            height: BUTTON_HEIGHT,
                            label: 'paypal',
                            borderRadius: 8
                        }}
                        fundingSource="paypal"
                    />
                </div>
            )
        }

        // Google Pay - use official PayPal/Google Pay button
        if (selectedMethod === 'google_pay') {
            return (
                <div className="flex flex-col gap-2" data-testid="booking-payment-google-pay-action">
                    <GooglePayButton
                        onCreateOrder={handleCreateOrder}
                        onApprove={handleApprove}
                        onError={handleError}
                        disabled={isLoading}
                        pricing={pricing}
                        onReady={handleGooglePayReady}
                        useWidgetButton={useWidgetButton}
                    />
                </div>
            )
        }

        if (selectedMethod === 'apple_pay') {
            return (
                <div className="flex flex-col gap-2" data-testid="booking-payment-apple-pay-action">
                    <ApplePayButton
                        onCreateOrder={handleCreateOrder}
                        onApprove={handleApprove}
                        onError={handleError}
                        disabled={isLoading}
                        pricing={pricing}
                        onReady={handleApplePayReady}
                        useWidgetButton={useWidgetButton}
                    />
                </div>
            )
        }
    }

    // Default rendering (legacy mode for showing all methods)
    if (!isResolved) {
        return <ComponentSpinner />
    }

    if (isRejected) {
        return (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <div className="flex items-center">
                    <div className="shrink-0">
                        <svg
                            className="h-5 w-5 text-red-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">PayPal Fehler</h3>
                        <p className="mt-1 text-sm text-red-700">
                            PayPal konnte nicht geladen werden. Bitte versuchen Sie es erneut.
                        </p>
                        <div className="flex justify-center mt-4">
                            <Button onClick={() => window.location.reload()}>Erneut versuchen</Button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="relative z-0 flex w-full min-w-0 max-w-full flex-col gap-4">
            {!allButtonsReady && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/75">
                    <ComponentSpinner />
                </div>
            )}
            <div
                className={cn(isLoading ? 'pointer-events-none opacity-50' : '', 'flex w-full min-w-0 flex-col gap-4')}
            >
                <div className="flex w-full min-w-0 flex-col gap-3">
                    {methodsToRender.includes('paypal') &&
                        (paypalWalletButtonsReady ? (
                            <div
                                className={cn(
                                    'w-full min-w-0',
                                    useWidgetButton ? 'widget-paypal-wallet-button-slot' : undefined
                                )}
                            >
                                <PayPalButtons
                                    createOrder={handleCreateOrder}
                                    onApprove={handleApprove}
                                    onError={handleError}
                                    onCancel={handleCancel}
                                    onInit={handlePayPalButtonsReady}
                                    disabled={isLoading}
                                    style={{
                                        disableMaxWidth: true,
                                        height: BUTTON_HEIGHT,
                                        label: 'paypal',
                                        borderRadius: 8
                                    }}
                                    fundingSource="paypal"
                                />
                            </div>
                        ) : (
                            <BrandButtonLoading method="paypal" />
                        ))}
                    {methodsToRender.includes('google_pay') && (
                        <div
                            className={cn(
                                'w-full min-w-0',
                                useWidgetButton ? 'widget-paypal-wallet-method-frame' : undefined
                            )}
                        >
                            <GooglePayButton
                                onCreateOrder={handleCreateOrder}
                                onApprove={handleApprove}
                                onError={handleError}
                                disabled={isLoading}
                                pricing={pricing}
                                onReady={handleGooglePayReady}
                                useWidgetButton={useWidgetButton}
                            />
                        </div>
                    )}
                    {methodsToRender.includes('apple_pay') && (
                        <div
                            className={cn(
                                'w-full min-w-0',
                                useWidgetButton ? 'widget-paypal-wallet-method-frame' : undefined
                            )}
                        >
                            <ApplePayButton
                                onCreateOrder={handleCreateOrder}
                                onApprove={handleApprove}
                                onError={handleError}
                                disabled={isLoading}
                                pricing={pricing}
                                onReady={handleApplePayReady}
                                useWidgetButton={useWidgetButton}
                            />
                        </div>
                    )}
                </div>
                {/* Only show divider when rendering card alongside other methods (not in single-method mode) */}
                {!selectedMethod &&
                    methodsToRender.includes('card') &&
                    (methodsToRender.includes('paypal') ||
                        methodsToRender.includes('google_pay') ||
                        methodsToRender.includes('apple_pay')) && (
                        <div
                            className={cn(
                                'relative flex items-center justify-center py-2',
                                useWidgetButton ? 'widget-paypal-card-divider' : undefined
                            )}
                        >
                            <div className="absolute inset-x-0 flex items-center">
                                <div
                                    className={cn(
                                        'w-full border-t',
                                        useWidgetButton ? 'widget-border' : 'border-gray-300'
                                    )}
                                />
                            </div>
                            <div
                                className={cn(
                                    'relative px-4 text-sm',
                                    useWidgetButton
                                        ? 'widget-bg-primary widget-text-secondary'
                                        : 'bg-white text-gray-500'
                                )}
                            >
                                oder
                            </div>
                        </div>
                    )}
                {methodsToRender.includes('card') && buttonReadyStates['card-fields'] ? (
                    <PayPalCardFieldsProvider
                        createOrder={handleCreateOrder}
                        onApprove={handleApprove}
                        onError={handleError}
                        onCancel={handleCancel}
                        style={buildPayPalCardFieldsStyle(useWidgetButton)}
                    >
                        <PayPalCardFieldsSection useWidgetButton={useWidgetButton} />
                    </PayPalCardFieldsProvider>
                ) : methodsToRender.includes('card') ? (
                    <ComponentSpinner />
                ) : null}
            </div>
        </div>
    )
}

// Loading state for brand buttons
function BrandButtonLoading({ method }: { method: string }) {
    const bgColor = method === 'paypal' ? 'bg-[#ffc439]' : 'bg-white border border-gray-300'

    return (
        <div
            className={cn('w-full flex items-center justify-center rounded-lg', bgColor)}
            style={{ height: BUTTON_HEIGHT }}
        >
            <ComponentSpinner className={method === 'paypal' ? 'text-[#003087]' : 'text-gray-400'} />
        </div>
    )
}

// Error state for brand buttons
function BrandButtonError({ method }: { method: string }) {
    const label = method === 'paypal' ? 'PayPal' : method === 'google_pay' ? 'Google Pay' : 'Apple Pay'

    return (
        <div
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm"
            style={{ height: BUTTON_HEIGHT }}
        >
            <span>{label} konnte nicht geladen werden</span>
        </div>
    )
}

function PayPalCardSubmitButton({ useWidgetButton = false }: { useWidgetButton?: boolean }) {
    const { cardFieldsForm } = usePayPalCardFields()

    const [isPaying, setIsPaying] = useState(false)

    const handleCardPayment = useCallback(async () => {
        if (!cardFieldsForm) {
            throw new Error('Unable to find any child components in the <PayPalCardFieldsProvider />')
        }

        const formState = await cardFieldsForm.getState()
        if (!formState.isFormValid) {
            // Show specific field errors
            const errors: string[] = []
            if (!formState.fields.cardNameField?.isValid) errors.push('Karteninhaber')
            if (!formState.fields.cardNumberField?.isValid) errors.push('Kartennummer')
            if (!formState.fields.cardExpiryField?.isValid) errors.push('Gültigkeitsdatum')
            if (!formState.fields.cardCvvField?.isValid) errors.push('CVV')

            if (errors.length > 0) {
                toast.error(`Bitte überprüfen Sie: ${errors.join(', ')}`)
            } else {
                toast.error('Bitte überprüfen Sie Ihre Kartendaten')
            }
            return
        }

        setIsPaying(true)

        // Submit the card form - errors are handled by PayPalCardFieldsProvider's onError callback
        cardFieldsForm.submit().catch((error) => {
            console.error('Error submitting card payment', error)
            setIsPaying(false)
            // Note: Don't show toast here - the onError callback in PayPalCardFieldsProvider handles it
        })
    }, [cardFieldsForm])

    if (useWidgetButton) {
        return (
            <WidgetButton
                type="button"
                disabled={isPaying}
                isLoading={isPaying}
                onClick={handleCardPayment}
                className="mt-3 min-h-11 rounded-lg text-base"
                data-testid="booking-payment-paypal-card-submit"
            >
                Buchung abschließen
            </WidgetButton>
        )
    }

    return (
        <button
            type="button"
            disabled={isPaying}
            onClick={handleCardPayment}
            className="w-full mt-3 h-11 rounded-lg bg-gray-800 hover:bg-gray-700 text-white font-semibold text-base transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
            {isPaying ? <ComponentSpinner className="size-5 text-white" /> : 'Buchung abschließen'}
        </button>
    )
}
